/**
 * Mirror of backend/src/common/branding-line.ts.
 *
 * The screen shows a line total before it is saved, so the arithmetic has to
 * exist on both sides. Two copies of a pricing rule is exactly how a preview
 * ends up disagreeing with the invoice, so
 * frontend/admin/tests/contracts/BrandingLineDrift.test.ts reads the backend
 * source and fails if the two ever diverge.
 *
 * dim1 is Height, dim2 is Width, dim3 is a slot length that never prices.
 */

export const BRANDING_LINE_BACKEND_FILE = 'backend/src/common/branding-line.ts';

export interface BrandingLineDimensions {
  readonly quantity: number;
  readonly dim1: number;
  readonly dim2: number;
  readonly dim3: number | null;
  readonly rateValue: number;
  readonly additionalAmount?: number;
}

export interface BrandingLineLabels {
  readonly uomName: string;
  readonly dim3UomName?: string | null;
  readonly brandingTypeName?: string | null;
  readonly brandingItemName?: string | null;
  readonly variantName?: string | null;
  /** Where the work stands. Printed for site work; a radio spot has no place. */
  readonly locationName?: string | null;
}

export type BrandingLineShape = 'AREA' | 'SLOT' | 'COUNT';

/**
 * Which fields a line needs, decided by the subtype's own unit — so the form
 * never asks the user to classify work the master already classified.
 */
const AREA_UNITS = new Set(['SQ_FT', 'SQ_M', 'RFT']);
const SLOT_UNITS = new Set(['SPOT', 'SECOND', 'DURATION']);

export function brandingBasisForUom(uomCode: string | null | undefined): BrandingLineShape {
  const code = (uomCode ?? '').trim().toUpperCase();
  if (AREA_UNITS.has(code)) return 'AREA';
  if (SLOT_UNITS.has(code)) return 'SLOT';
  return 'COUNT';
}

/**
 * What a sub-type says each of its three dimensions measures.
 *
 * The unit used to imply this: SQ_FT meant height by width, SPOT meant a slot
 * length, anything else meant a bare count. That inference is kept below, in
 * brandingPlanForUom, because it still seeds a sub-type that has never been
 * told otherwise -- but a sub-type may now say so for itself, and a newspaper
 * bought by column-centimetres can finally record the height it was bought at.
 *
 * The first two name lengths because only lengths multiply into the amount.
 * The third names time, and is never priced.
 */
export type BrandingLengthMeaning = 'HEIGHT' | 'WIDTH' | 'NA';
export type BrandingTimeMeaning = 'DURATION' | 'NA';

export interface BrandingMeasurementPlan {
  readonly dim1: BrandingLengthMeaning;
  readonly dim2: BrandingLengthMeaning;
  readonly dim3: BrandingTimeMeaning;
}

/** The plan a unit implies, which is how every sub-type was first seeded. */
export function brandingPlanForUom(
  uomCode: string | null | undefined,
): BrandingMeasurementPlan {
  switch (brandingBasisForUom(uomCode)) {
    case 'AREA':
      return { dim1: 'HEIGHT', dim2: 'WIDTH', dim3: 'NA' };
    case 'SLOT':
      return { dim1: 'NA', dim2: 'NA', dim3: 'DURATION' };
    default:
      return { dim1: 'NA', dim2: 'NA', dim3: 'NA' };
  }
}

/** The unit a plan asks for, so the two can never drift apart on a screen. */
export function brandingUomForPlan(
  plan: BrandingMeasurementPlan,
  current?: string | null,
): string {
  const code = (current ?? '').trim().toUpperCase();
  switch (brandingBasisForPlan(plan)) {
    case 'AREA':
      return AREA_UNITS.has(code) ? code : 'SQ_FT';
    case 'SLOT':
      return SLOT_UNITS.has(code) ? code : 'SPOT';
    default:
      return code && brandingBasisForUom(code) === 'COUNT' ? code : 'NOS';
  }
}

export function brandingBasisForPlan(
  plan: BrandingMeasurementPlan,
): BrandingLineShape {
  if (plan.dim1 !== 'NA' || plan.dim2 !== 'NA') return 'AREA';
  if (plan.dim3 === 'DURATION') return 'SLOT';
  return 'COUNT';
}

/** Height, Width or nothing -- the word a screen puts above each box. */
export function brandingDimensionLabel(
  meaning: BrandingLengthMeaning | BrandingTimeMeaning,
): string {
  switch (meaning) {
    case 'HEIGHT':
      return 'Height';
    case 'WIDTH':
      return 'Width';
    case 'DURATION':
      return 'Duration';
    default:
      return '';
  }
}

/**
 * The same refusals as brandingLineProblems, but against what the sub-type
 * declared rather than what its unit implied. A dimension the sub-type does
 * not name must stay at 1, because it still multiplies into the amount.
 */
export function brandingLineProblemsForPlan(
  d: BrandingLineDimensions,
  plan: BrandingMeasurementPlan,
  opts?: BrandingLineOptions,
): readonly string[] {
  const problems: string[] = [];
  const totalArea =
    brandingBasisForPlan(plan) === 'AREA' && opts?.areaEntry === 'TOTAL';

  if (d.quantity <= 0) problems.push('Quantity must be more than zero.');
  if (d.rateValue <= 0) problems.push('Rate must be more than zero.');

  const lengths: readonly [BrandingLengthMeaning, number, string][] = [
    [plan.dim1, d.dim1, 'first'],
    [plan.dim2, d.dim2, 'second'],
  ];
  for (const [meaning, value] of lengths) {
    if (totalArea) {
      if (value !== 1) {
        problems.push(
          'This document takes the finished area as the quantity, not a height and width.',
        );
      }
    } else if (meaning === 'NA') {
      if (value !== 1) {
        problems.push('This sub-type is not measured that way.');
      }
    } else if (value <= 0) {
      problems.push(`${brandingDimensionLabel(meaning)} is needed for this sub-type.`);
    }
  }

  if (plan.dim3 === 'DURATION') {
    if (d.dim3 === null || d.dim3 === undefined || d.dim3 <= 0) {
      problems.push('Slot length is needed for broadcast work.');
    }
  } else if (d.dim3 !== null && d.dim3 !== undefined) {
    problems.push('Slot length applies only to broadcast work.');
  }

  return [...new Set(problems)];
}

export type BrandingEvidenceKind = 'INSTALLATION_PHOTOS' | 'BROADCAST_REPORT' | 'RECEIPT_COPY';

/** Photographs for area work, a telecast report for a slot, a receipt for goods. */
export function brandingEvidenceKind(uomCode: string | null | undefined): BrandingEvidenceKind {
  switch (brandingBasisForUom(uomCode)) {
    case 'AREA':
      return 'INSTALLATION_PHOTOS';
    case 'SLOT':
      return 'BROADCAST_REPORT';
    default:
      return 'RECEIPT_COPY';
  }
}

/** Which fields a verification level may edit, given the basis. */
export function brandingVerifiableFields(
  uomCode: string | null | undefined,
): readonly ('quantity' | 'dim1' | 'dim2' | 'dim3' | 'location')[] {
  switch (brandingBasisForUom(uomCode)) {
    case 'AREA':
      return ['location', 'quantity', 'dim1', 'dim2'];
    case 'SLOT':
      return ['quantity'];
    default:
      return ['quantity'];
  }
}

export function brandingLineShape(d: BrandingLineDimensions): BrandingLineShape {
  if (d.dim1 !== 1 || d.dim2 !== 1) return 'AREA';
  if (d.dim3 !== null && d.dim3 !== undefined) return 'SLOT';
  return 'COUNT';
}

/**
 * How a document captures area work. A branding request is raised before the
 * work exists and is described as height x width; a work order is issued
 * against an agreed finished area. Default keeps every existing caller as-is.
 */
export interface BrandingLineOptions {
  readonly areaEntry?: 'DIMENSIONS' | 'TOTAL';
}

export function brandingLineProblems(
  d: BrandingLineDimensions,
  uomCode: string | null | undefined,
  opts?: BrandingLineOptions,
): readonly string[] {
  const problems: string[] = [];
  const basis = brandingBasisForUom(uomCode);
  const totalArea = basis === 'AREA' && opts?.areaEntry === 'TOTAL';

  if (d.quantity <= 0) problems.push('Quantity must be more than zero.');
  if (d.rateValue <= 0) problems.push('Rate must be more than zero.');

  if (totalArea) {
    if (d.dim1 !== 1 || d.dim2 !== 1) {
      problems.push(
        'This document takes the finished area as the quantity, not a height and width.',
      );
    }
  } else if (basis === 'AREA') {
    if (d.dim1 <= 0) problems.push('Height is needed for work measured by area.');
    if (d.dim2 <= 0) problems.push('Width is needed for work measured by area.');
  } else if (d.dim1 !== 1 || d.dim2 !== 1) {
    problems.push('Height and width apply only to work measured by area.');
  }

  if (basis === 'SLOT') {
    if (d.dim3 === null || d.dim3 === undefined || d.dim3 <= 0) {
      problems.push('Slot length is needed for broadcast work.');
    }
  } else if (d.dim3 !== null && d.dim3 !== undefined) {
    problems.push('Slot length applies only to broadcast work.');
  }

  return problems;
}

export function brandingLineBaseAmount(d: BrandingLineDimensions): number {
  return round2(d.rateValue * d.quantity * d.dim1 * d.dim2);
}

export function brandingLineTotalAmount(d: BrandingLineDimensions): number {
  return round2(brandingLineBaseAmount(d) + (d.additionalAmount ?? 0));
}

export function brandingLineMeasuredQuantity(d: BrandingLineDimensions): number {
  return round3(d.quantity * d.dim1 * d.dim2);
}

export function brandingLineDescription(
  d: BrandingLineDimensions,
  labels: BrandingLineLabels,
): string {
  const what = [labels.brandingItemName, labels.variantName]
    .filter((x): x is string => Boolean(x && x.trim()))
    .join(' - ');
  const measure = describeMeasure(d, labels);
  let line = what ? `${what}, ${measure}` : measure;

  const place = (labels.locationName ?? '').trim();
  if (place) line += ` at - ${place}`;

  return line;
}

/**
 * The word for one piece of area work -- one wall, one board -- and for any
 * line whose unit has no name. The backend's description prints the same word.
 */
export const BRANDING_PIECE_LABEL = 'Unit';
/** What a slot length is counted in when the line does not say. */
export const BRANDING_SLOT_FALLBACK_LABEL = 'Second';

/**
 * What a line counts and what it measures, each with its own unit.
 *
 * An area line orders a number of pieces (1 wall) whose size gives the area
 * (20 x 50 = 1000 Sq Ft); the line's unit belongs to the area, never to the
 * count, so the count is a plain number. Broadcast and counted work count in
 * the line's own unit. Display only: the bill's description below is built
 * from this same reading, so a screen and the bill cannot put a unit against
 * different numbers.
 */
export interface BrandingLineMeasure {
  readonly shape: BrandingLineShape;
  readonly count: number;
  /** Null for area work: pieces are counted, not measured. */
  readonly countUnit: string | null;
  /** Finished area, for area work only. */
  readonly measured: number | null;
  readonly measuredUnit: string | null;
  /** Slot length, for broadcast work only. Never priced. */
  readonly slot: number | null;
  readonly slotUnit: string | null;
}

export function brandingLineMeasure(
  d: BrandingLineDimensions,
  labels: Pick<BrandingLineLabels, 'uomName' | 'dim3UomName'>,
): BrandingLineMeasure {
  const uom = labels.uomName || BRANDING_PIECE_LABEL;
  const shape = brandingLineShape(d);
  const none = { measured: null, measuredUnit: null, slot: null, slotUnit: null };
  switch (shape) {
    case 'AREA':
      return {
        ...none,
        shape,
        count: d.quantity,
        countUnit: null,
        measured: brandingLineMeasuredQuantity(d),
        measuredUnit: uom,
      };
    case 'SLOT':
      return {
        ...none,
        shape,
        count: d.quantity,
        countUnit: uom,
        slot: d.dim3 as number,
        slotUnit: labels.dim3UomName || BRANDING_SLOT_FALLBACK_LABEL,
      };
    default:
      return { ...none, shape, count: d.quantity, countUnit: uom };
  }
}

function describeMeasure(d: BrandingLineDimensions, labels: BrandingLineLabels): string {
  const m = brandingLineMeasure(d, labels);
  switch (m.shape) {
    case 'AREA':
      return `${trim(m.count)} ${BRANDING_PIECE_LABEL}(s) ${trim(d.dim1)}X${trim(d.dim2)} = ${trim(
        m.measured as number,
      )} ${m.measuredUnit}`;
    case 'SLOT':
      return `${trim(m.count)} ${m.countUnit}(s) of ${trim(m.slot as number)} ${m.slotUnit}(s)`;
    default:
      return `${trim(m.count)} ${m.countUnit}(s)`;
  }
}

/** What makes two lines of one order the same work at the same place. */
export interface BrandingPlacedLine {
  readonly brandingTypeId: string;
  readonly brandingItemId?: string | null;
  readonly otherDetail?: string | null;
  readonly geoLevelCode?: string | null;
  readonly geoNodeId?: string | null;
}

/**
 * A work order carries one line per geography for a piece of work, holding the
 * total quantity for that place (owner, 23 Oct). Returns the 1-based numbers
 * of the first line repeated and the line repeating it, or null.
 */
export function brandingRepeatedPlace(
  lines: readonly BrandingPlacedLine[],
): readonly [number, number] | null {
  const seen = new Map<string, number>();
  for (let i = 0; i < lines.length; i += 1) {
    const l = lines[i];
    const key = [
      l.brandingTypeId,
      l.brandingItemId ?? '',
      (l.otherDetail ?? '').trim().toLowerCase(),
      l.geoLevelCode ?? '',
      l.geoNodeId ?? '',
    ].join('|');
    const first = seen.get(key);
    if (first !== undefined) return [first, i + 1];
    seen.set(key, i + 1);
  }
  return null;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function round3(n: number): number {
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}

function trim(n: number): string {
  return Number(n.toFixed(3)).toString();
}
